import { useState, useEffect, useCallback } from 'react';

import { REPORT_TYPES, report } from '@adsk/exo-skeleton-api';
import {
  safeStringify,
  wrapWithEventPropertiesPrefix,
} from '@adsk/exo-skeleton-api/internal';

import { EXO_SKELETON_SCOPE_NAME } from '../consts';
import { setIsUserOfflineToErrorContext } from '../utils/monitoring';

type NetworkStatus = {
  online: boolean;
  since: Date;
};

// this is still experimental in some browsers, so we need to check for it
const getNetworkConnectionInfo = () => {
  if (!navigator) {
    return null;
  }

  if ('connection' in navigator) {
    return navigator.connection;
  } else if ('mozConnection' in navigator) {
    return navigator.mozConnection;
  } else if ('webkitConnection' in navigator) {
    return navigator.webkitConnection;
  }

  return null;
};

// in some browsers, the connection info cannot be cloned, so we need to parse it
const parseNetworkConnectionInfo = (connectionInfo: unknown) => {
  try {
    return JSON.parse(JSON.stringify(connectionInfo));
  } catch {
    // ignore error
    return undefined;
  }
};

const NETWORK_STATUS_REPORT_NAME = 'network-status-report';

export const useNetworkStatus = () => {
  const [networkStatuses, setNetworkStatuses] = useState<NetworkStatus[]>([]);

  const clearNetworkStatuses = useCallback(() => {
    setNetworkStatuses([]);
  }, []);

  const addNetworkStatus = useCallback(
    (status: NetworkStatus) => {
      setNetworkStatuses((prev) => [...prev, status]);
    },
    [setNetworkStatuses],
  );

  const handleOnline = useCallback(() => {
    const connectionInfo = getNetworkConnectionInfo();
    const currentNetworkStatus = {
      online: true,
      since: new Date(),
    };

    const context = {
      connectionInfo: parseNetworkConnectionInfo(connectionInfo),
      statuses: [...networkStatuses, currentNetworkStatus],
    };

    void report({
      scope: EXO_SKELETON_SCOPE_NAME,
      type: REPORT_TYPES.info,
      value: NETWORK_STATUS_REPORT_NAME,
      context: {
        ...context,
        ...wrapWithEventPropertiesPrefix({
          data: safeStringify(context),
          message: NETWORK_STATUS_REPORT_NAME,
          type: REPORT_TYPES.info,
        }),
      },
    });

    clearNetworkStatuses();
    setIsUserOfflineToErrorContext(false);
  }, [clearNetworkStatuses, networkStatuses]);

  const handleOffline = useCallback(() => {
    addNetworkStatus({ online: false, since: new Date() });
    setIsUserOfflineToErrorContext(true);
  }, [addNetworkStatus]);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return null;
};
